/** Pure helpers for the supplier BN-timeline editor (INP-508).
 *
 * Periods are half-open `[valid_from, valid_to)`; `valid_to === null` is
 * the current, open-ended period. Dates are ISO `YYYY-MM-DD` strings as
 * the API serializes them. Kept framework-free so vitest can cover the
 * boundary logic without a browser drive. */

export interface BnPeriodLike {
  valid_from: string;
  valid_to: string | null;
}

export function isCurrentPeriod(p: BnPeriodLike): boolean {
  return p.valid_to === null;
}

/** "2020-01-01 → 2023-07-01" for a bounded period; the open-ended one
 *  reads "2020-01-01 → current". */
export function formatPeriodRange(p: BnPeriodLike): string {
  return `${p.valid_from} → ${p.valid_to ?? 'current'}`;
}

/** Ascending by `valid_from`; the open-ended period sorts last on ties by
 *  falling back to a far-future end. Non-mutating. */
export function sortPeriodsAscending<T extends BnPeriodLike>(periods: readonly T[]): T[] {
  return [...periods].sort((a, b) => {
    if (a.valid_from !== b.valid_from) return a.valid_from < b.valid_from ? -1 : 1;
    const aEnd = a.valid_to ?? '9999-12-31';
    const bEnd = b.valid_to ?? '9999-12-31';
    return aEnd < bEnd ? -1 : aEnd > bEnd ? 1 : 0;
  });
}

/** Half-open overlap on ISO date strings (lexicographic compare is valid
 *  for `YYYY-MM-DD`). `null` end = open to infinity. */
export function periodsOverlap(a: BnPeriodLike, b: BnPeriodLike): boolean {
  const aEnd = a.valid_to ?? '9999-12-31';
  const bEnd = b.valid_to ?? '9999-12-31';
  return a.valid_from < bEnd && b.valid_from < aEnd;
}

/** The BN a supplier had on `date` — the period whose half-open
 *  `[valid_from, valid_to)` covers it. Falls back to the current
 *  open-ended BN when no date is given or none covers it, then to the
 *  latest period. `null` when the supplier has no BN periods at all.
 *  Used to default an invoice's BN from the supplier registry. */
export function bnForDate(
  periods: readonly (BnPeriodLike & { bn: string })[],
  date: string | null,
): string | null {
  if (date) {
    const covering = periods.find(
      (p) => p.valid_from <= date && (p.valid_to === null || date < p.valid_to),
    );
    if (covering) return covering.bn;
  }
  const current = periods.find(isCurrentPeriod);
  if (current) return current.bn;
  const sorted = sortPeriodsAscending(periods);
  return sorted.length > 0 ? sorted[sorted.length - 1].bn : null;
}

export type BnPeriodValidation = { ok: true } | { ok: false; reason: string };

/** Client-side pre-check mirroring the server's window invariants, so the
 *  form can flag a bad range before the round-trip. The server remains
 *  the source of truth (409 on violation) — this is just fast feedback.
 *
 *  A new open-ended period is allowed to abut an existing open period
 *  (the server closes the prior one at `valid_from`); it's rejected only
 *  when it would start on/before that open period. A bounded period must
 *  not overlap any existing period. */
export function validateNewPeriod(
  candidate: BnPeriodLike & { bn: string },
  existing: readonly BnPeriodLike[],
): BnPeriodValidation {
  if (!candidate.bn.trim()) {
    return { ok: false, reason: 'BN is required.' };
  }
  if (!candidate.valid_from) {
    return { ok: false, reason: 'Start date is required.' };
  }
  if (candidate.valid_to !== null && candidate.valid_to <= candidate.valid_from) {
    return { ok: false, reason: 'End date must be after the start date.' };
  }

  if (candidate.valid_to === null) {
    const openPeriod = existing.find(isCurrentPeriod);
    if (openPeriod && candidate.valid_from <= openPeriod.valid_from) {
      return {
        ok: false,
        reason: `A new current BN must start after the existing open period (opens ${openPeriod.valid_from}).`,
      };
    }
    // A new open period abutting/after the existing open period is fine —
    // the server closes the prior one. Only bounded periods block it.
    const boundedOverlap = existing
      .filter((p) => !isCurrentPeriod(p))
      .find((p) => periodsOverlap(candidate, p));
    if (boundedOverlap) {
      return { ok: false, reason: `Overlaps ${formatPeriodRange(boundedOverlap)}.` };
    }
    return { ok: true };
  }

  const overlap = existing.find((p) => periodsOverlap(candidate, p));
  if (overlap) {
    return { ok: false, reason: `Overlaps ${formatPeriodRange(overlap)}.` };
  }
  return { ok: true };
}
